import { CardContent } from "@/components/ui/card";
import { CollapsibleCard } from "@/components/CollapsibleSection";
import { fmtDateTime } from "@/lib/dates";

/**
 * The complete history of one case: the progress ladder, then every event behind it.
 *
 * Replaces a list that printed the action name and a timestamp and nothing else — so "assigned"
 * appeared with no indication of who assigned it, to whom, or from what. Both halves come from
 * `GET /admin/transactions/{tid}/audit`; the milestones are derived server-side (core/audit.py)
 * so this page and anything else asking "when was it submitted" cannot answer differently.
 */

// Human wording for each action. The server stores identifiers — `review_reset_on_reassign` is the
// right name in a database and the wrong one on a page someone reads to understand a case.
const LABELS = {
  created: "Document created",
  draft_edited: "Draft edited",
  cart_added: "Added to cart",
  cart_removed: "Removed from cart",
  payment_completed: "Payment completed",
  payment_failed: "Payment failed",
  refunded: "Payment refunded",
  status_update: "Status changed",
  assigned: "Assigned to advocate",
  reviewed_by_update: "Review channel set",
  review_started: "Review started",
  review_submitted: "Review submitted",
  review_accepted: "Review accepted",
  review_rework_requested: "Returned for rework",
  review_findings_applied: "Findings applied to document",
  review_reset_on_reassign: "Review reset on reassignment",
  document_edited: "Document edited by admin",
  file_uploaded: "File attached",
  file_deleted: "File removed",
  report_visibility_changed: "Report visibility changed",
  softcopy_generated: "Soft copy generated",
  softcopy_delivered: "Soft copy uploaded",
  softcopy_waived: "Soft copy skipped — fulfilling by delivery",
  delivery_status_update: "Delivery updated",
  comment_posted: "Message posted",
  quick_message_sent: "Quick message to advocate",
  reviewer_channel_opened: "Advocate channel opened",
  reviewer_channel_closed: "Advocate channel closed",
  email_sent: "Notification sent",
  profile_updated: "Customer details updated",
  email_changed_via_provider: "Email updated by sign-in provider",
  password_retired_on_link: "Password retired — provider sign-in only",
  deleted: "Draft deleted",
  denied: "Action refused",
};

const ROLE_STYLE = {
  customer: "bg-[#2563EB] text-white",
  reviewer: "bg-[#CBA96C] text-white",
  admin: "bg-[#2D3B57] text-white",
  system: "bg-[#6B7280] text-white",
};
const ROLE_LABEL = { customer: "Customer", reviewer: "Advocate", admin: "Admin", system: "System" };

/** "3d 4h" / "12m" — the gap between two milestones, which is what a queue question is really about. */
export function humanDuration(seconds) {
  if (seconds == null) return null;
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60), h = Math.floor(m / 60), d = Math.floor(h / 24);
  if (d > 0) return `${d}d ${h % 24}h`;
  if (h > 0) return `${h}h ${m % 60}m`;
  return `${m}m`;
}

/**
 * Who the elapsed time belonged to, under the ladder and inside the same card.
 *
 * The per-rung "+2d 4h" answers "how long was this step", which is the right number when you are
 * looking at one step. It does not answer "where did the fortnight go" — for that the gaps have to
 * be added up per party, which is what the server does in `core.audit.summarise`.
 *
 * `courier` is listed beside the three roles rather than folded into admin: admin records the
 * delivery, but the days before it are transit, and billing those to admin makes every delivered
 * case look like it sat on someone's desk.
 */
const HELD_BY_ORDER = [
  ["customer", "Customer"],
  ["admin", "Admin"],
  ["reviewer", "Advocate"],
  ["courier", "In transit"],
  ["system", "System"],
];

function TimeByRole({ byRole, totalSeconds }) {
  const rows = HELD_BY_ORDER
    .filter(([key]) => (byRole?.[key] ?? 0) > 0)
    .map(([key, label]) => [key, label, byRole[key]]);
  // Nothing to apportion until a second milestone lands — one rung has no gap before it.
  if (!rows.length) return null;
  const total = rows.reduce((sum, [, , secs]) => sum + secs, 0);
  return (
    <div className="mt-4 pt-4 border-t border-white/5" data-testid="case-time-by-role">
      <div className="text-[10px] tracking-[0.24em] uppercase text-[#6B7280] mb-2.5">Time held by</div>
      <div className="space-y-1.5">
        {rows.map(([key, label, secs]) => (
          <div key={key} className="flex items-center gap-2 text-xs" data-testid={`held-by-${key}`}>
            <span className="text-[#A0AABF] w-20 shrink-0">{label}</span>
            {/* Proportion of the apportioned time, so the longest pole is visible without
                comparing numbers. Width only — the figure beside it is the actual answer. */}
            <span className="flex-1 h-1 rounded-full bg-white/5 overflow-hidden max-w-[160px]">
              <span className="block h-full bg-[#CBA96C]/70"
                    style={{ width: `${total ? Math.max(2, (secs / total) * 100) : 0}%` }}/>
            </span>
            <span className="text-[#CBA96C] tabular-nums">{humanDuration(secs)}</span>
          </div>
        ))}
      </div>
      {totalSeconds != null && (
        <div className="flex items-center gap-2 text-xs mt-2.5 pt-2.5 border-t border-white/5"
             data-testid="held-by-total">
          <span className="text-white/80 w-20 shrink-0">Total</span>
          <span className="flex-1 max-w-[160px]"/>
          <span className="text-white/90 tabular-nums">{humanDuration(totalSeconds)}</span>
        </div>
      )}
    </div>
  );
}

/**
 * The ladder, grouped by whose rung it is.
 *
 * A flat chronological ladder answers "what has happened"; it does not answer "who is this
 * waiting on", which is the question an admin opens this card with. Each milestone carries an
 * `owner` from the server (`core.audit.summarise`) — the actor who reached it, or where it is
 * expected to sit while still pending — so the rungs sort into Customer / Admin / Advocate / In
 * transit, each with its own total.
 *
 * Within a group the rungs stay in ladder order, so the sequence is still readable; it is only the
 * grouping that departs from chronology. A group with no rungs at all is not drawn.
 */
function GroupedMilestones({ milestones, byRole }) {
  const groups = HELD_BY_ORDER
    .map(([role, label]) => [role, label, milestones.filter(m => (m.owner || "admin") === role)])
    .filter(([, , rungs]) => rungs.length > 0);
  // Any owner the canonical order does not name (a role added later) would otherwise vanish
  // silently, so anything left over is drawn under its own raw key rather than dropped.
  const named = new Set(HELD_BY_ORDER.map(([role]) => role));
  const leftovers = milestones.filter(m => !named.has(m.owner || "admin"));
  const extra = [...new Set(leftovers.map(m => m.owner))]
    .map(role => [role, ROLE_LABEL[role] || role, leftovers.filter(m => m.owner === role)]);

  return (
    <div className="space-y-5" data-testid="case-milestones-grouped">
      {[...groups, ...extra].map(([role, label, rungs]) => (
        <div key={role} data-testid={`milestone-group-${role}`}>
          <div className="flex items-baseline justify-between gap-3 mb-2">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#A0AABF]">{label}</div>
            {(byRole?.[role] ?? 0) > 0 && (
              <div className="text-[11px] text-[#CBA96C] tabular-nums"
                   data-testid={`milestone-group-total-${role}`}>
                {humanDuration(byRole[role])}
              </div>
            )}
          </div>
          <Milestones milestones={rungs} />
        </div>
      ))}
    </div>
  );
}

function Milestones({ milestones }) {
  return (
    <ol className="space-y-0" data-testid="case-milestones">
      {milestones.map((m, i) => (
        <li key={m.key} className="flex gap-3">
          {/* Rail: a dot per rung, joined by a line, so the unreached ones read as "not yet"
              rather than as missing data. */}
          <div className="flex flex-col items-center">
            <span className={`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ${
              m.reached ? "bg-[#CBA96C]" : "bg-white/15"}`} />
            {i < milestones.length - 1 && (
              <span className={`w-px flex-1 my-1 ${m.reached ? "bg-[#CBA96C]/40" : "bg-white/10"}`} />
            )}
          </div>
          <div className={`flex-1 flex flex-wrap items-baseline gap-x-2 pb-3 text-xs ${
            m.reached ? "" : "opacity-45"}`}>
            <span className="font-medium">{m.label}</span>
            {m.reached ? (
              <>
                <span className="text-[#6B7280]">{fmtDateTime(m.ts)}</span>
                {/* No actor here any more. The group heading above already names the party, so
                    repeating "· Admin (Admin)" on every rung was the same word three times, and
                    which *individual* did it is still one section down in Full history, where every
                    event carries its actor. The rung is left saying what happened, when, and how
                    long it took. */}
                {m.elapsed_seconds != null && (
                  <span className="text-[#CBA96C]">+{humanDuration(m.elapsed_seconds)}</span>
                )}
              </>
            ) : <span className="text-[#6B7280]">pending</span>}
          </div>
        </li>
      ))}
    </ol>
  );
}

// "POST /transactions/3d926a91-121c-4fac-.../submit-review" is not a sentence anyone reads. The
// case id is already the page you are on, so it carries no information here.
const shortAttempt = (a) => (a || "").replace(/\/[0-9a-f]{8}-[0-9a-f-]{27,}/gi, "/…");

// Fields whose *value* is a whole document, not a fact — the body markdown, admin's free-form
// notes, extra clauses, the raw form data blob. The action label already says what happened
// ("Document edited by admin"); printing the first hundred characters of a legal document's markup
// underneath it because it happens to be the field that changed is noise, not a changes summary.
const OPAQUE_FIELDS = new Set(["body", "extra_clauses", "admin_notes", "form_data"]);

/** A from/to value, short enough to read as a summary rather than a story — long values are
 *  genuinely truncated, not just wrapped, so one edit's diff can't push the whole history open. */
const summarizeValue = (v) => {
  if (v === undefined || v === null || v === "") return "—";
  const s = String(v);
  return s.length > 60 ? `${s.slice(0, 60)}…` : s;
};

function Event({ e }) {
  const denied = e.outcome === "denied";
  const initial = (e.actor_name || "?")[0].toUpperCase();
  return (
    <div className="flex gap-3 py-2.5 border-b border-dashed border-white/10 last:border-0"
         data-testid="case-event">
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
        ROLE_STYLE[e.actor_role] || ROLE_STYLE.system}`}>{initial}</div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className={`text-xs font-medium ${denied ? "text-[#EF4444]" : ""}`}>
            {denied ? `Refused: ${shortAttempt(e.attempted)}` : (LABELS[e.action] || e.action)}
          </span>
          <span className="text-[11px] text-[#6B7280]">
            {e.actor_name} · {ROLE_LABEL[e.actor_role] || e.actor_role}
          </span>
          <span className="text-[11px] text-[#6B7280] ml-auto shrink-0">{fmtDateTime(e.ts)}</span>
        </div>
        {/* from -> to, the half the old log threw away — but not for a whole document body (see
            OPAQUE_FIELDS): the label above already says what happened. */}
        {e.field && !OPAQUE_FIELDS.has(e.field) && (
          <div className="text-[11px] text-[#A0AABF] mt-0.5">
            {e.field}: <span className="text-[#6B7280]">{summarizeValue(e.from)}</span> → <span className="text-white/90">{summarizeValue(e.to)}</span>
          </div>
        )}
        {(e.note || e.detail) && (
          <div className="text-[11px] text-[#A0AABF] mt-0.5 break-words">{e.note || e.detail}</div>
        )}
        {Array.isArray(e.changes) && e.changes.some(c => !OPAQUE_FIELDS.has(c.field)) && (
          <ul className="mt-1 space-y-0.5">
            {e.changes.filter(c => !OPAQUE_FIELDS.has(c.field)).map((c, i) => (
              <li key={i} className="text-[11px] text-[#A0AABF] break-words">
                <span className="text-[#6B7280]">{c.field}:</span> {summarizeValue(c.from)} → <span className="text-white/90">{summarizeValue(c.to)}</span>
              </li>
            ))}
          </ul>
        )}
        {Array.isArray(e.invalidated) && e.invalidated.length > 0 && (
          <div className="text-[11px] text-[#EF4444] mt-0.5">invalidated: {e.invalidated.join(", ")}</div>
        )}
      </div>
    </div>
  );
}

export default function CaseHistory({ history }) {
  if (!history) return null;
  const events = [...(history.events || [])].reverse();   // newest first for reading
  return (
    /* **One card, two readings of the same record.** Progress is the milestone ladder; Full
       history is every event behind it — the summary and its source, not two subjects. Separate
       cards let the summary be read with its evidence folded away, which is the one combination
       that invites trusting a milestone nobody can check.

       The count stays on the header so a collapsed card still says how much is behind it;
       otherwise closing it hides the fact that decides whether to open it again. */
    <CollapsibleCard
      id="case-history" testid="case-history" title="Case history"
      actions={<span className="text-xs text-[#6B7280]">{history.event_count} events</span>}
    >
      <CardContent>
        <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-6">
          <div className="lg:border-r lg:border-white/5 lg:pr-6">
            <div className="text-[10px] tracking-[0.24em] uppercase text-[#6B7280] mb-3">Progress</div>
            <GroupedMilestones milestones={history.milestones || []} byRole={history.time_by_role} />
            {/* Same card, under the grouped ladder: the groups say who each step belonged to, this
                compares them side by side and gives the case total. Two readings of one record,
                which is the whole point of this card — see the note at the top. */}
            <TimeByRole byRole={history.time_by_role} totalSeconds={history.total_seconds} />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.24em] uppercase text-[#6B7280] mb-3">Full history</div>
            {events.length === 0
              ? <div className="text-xs text-[#6B7280]">Nothing recorded yet.</div>
              : <div className="max-h-[28rem] overflow-y-auto pr-1">{events.map((e, i) => <Event key={i} e={e} />)}</div>}
          </div>
        </div>
      </CardContent>
    </CollapsibleCard>
  );
}
